import React, { useEffect, useMemo, useState } from 'react';
import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useSearchUsers } from '../../../hooks';
import { DEBOUNCE_TIME } from '../../../constants';
import { TeamAdminsFieldProps } from './types';
import { getUserLabel, getUserOptionText, toOption } from './teamAdmins.utils';

export function TeamAdminsField({
  value,
  onChange,
  disabled,
  error,
  helperText,
}: TeamAdminsFieldProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setDebouncedSearch(inputValue),
      DEBOUNCE_TIME,
    );
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const { data: searchResults = [], isFetching } =
    useSearchUsers(debouncedSearch);

  useEffect(() => {
    if (!searchResults.length) return;
    setKnownNames(prev => {
      const next = { ...prev };
      for (const user of searchResults) next[user.email] = user.username;
      return next;
    });
  }, [searchResults]);

  const selectedOptions = useMemo(
    () => value.map(email => toOption(email, knownNames)),
    [value, knownNames],
  );

  return (
    <Autocomplete
      multiple
      id="org-team-admins"
      options={searchResults}
      getOptionLabel={getUserLabel}
      getOptionSelected={(option, selected) => option.email === selected.email}
      renderOption={getUserOptionText}
      filterOptions={options =>
        options.filter(option => !value.includes(option.email))
      }
      value={selectedOptions}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason === 'input') setInputValue(nextValue);
      }}
      onChange={(_, nextValue) => {
        const names = { ...knownNames };
        for (const user of nextValue) names[user.email] = user.username;
        setKnownNames(names);
        onChange(nextValue.map(user => user.email));
        setInputValue('');
      }}
      loading={isFetching}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label="Team Admins"
          placeholder={!value.length ? 'Team Admins' : ''}
          data-testid="org-team-admins-input"
          InputLabelProps={{ shrink: true }}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
